import { FC, useEffect, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  List,
  ListItem,
  Chip,
  ButtonGroup,
  Checkbox,
  ListItemText,
  Box,
  TextField,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Search as SearchIcon, Close as CloseIcon } from '@material-ui/icons';
import { RootState } from '../../store';
import { InputField } from '../input-field';
import { moveGetAll } from '../../api/move';
import { addSelectedMove, removeSelectedMove, clearSelectedMoves } from '../../slices/unknownSlice';
import { TableItemMenu } from '../table-item-menu';
import { openMoveDialog } from '../../slices/moveSlice';

interface UnknownMovesPanelProps {
  height: number;
  onHide?: () => void;
}

export const UnknownMovesPanel: FC<UnknownMovesPanelProps> = ({ height, onHide }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { selectedMoves } = useSelector((state: RootState) => state.unknown);
  const { moveDialogOpen } = useSelector((state: RootState) => state.move);

  const [moves, setMoves] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchedString, setSearchedString] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getMoves();
  }, []);

  useEffect(() => {
    getMoves();
  }, [searchedString]);

  // Refresh after the shared New Action dialog closes. It resets moveStatus
  // before closing, so we refresh on the open->close transition instead of
  // relying on the status still being 'success' at that instant.
  // When the dialog was opened from this panel's "New action" button
  // (create mode), also auto-select the action that was just created so it
  // lands directly in "Tactical Actions to reduce this Unknown". The new
  // action is identified by diffing the refreshed list against the ids that
  // existed when "New action" was clicked (movePost does not expose the new
  // id). Edit/view/duplicate from the context menu do not set the flag, so
  // they are never auto-selected.
  const wasMoveDialogOpenRef = useRef(false);
  const createInitiatedRef = useRef(false);
  const existingMoveIdsRef = useRef<Set<any>>(new Set());
  useEffect(() => {
    if (wasMoveDialogOpenRef.current && !moveDialogOpen) {
      const shouldAutoSelectCreated = createInitiatedRef.current;
      createInitiatedRef.current = false;
      getMoves().then((items) => {
        if (shouldAutoSelectCreated) {
          items
            .filter((move) => !existingMoveIdsRef.current.has(move.id))
            .forEach((move) => dispatch(addSelectedMove(move)));
        }
      });
    }
    wasMoveDialogOpenRef.current = moveDialogOpen;
  }, [moveDialogOpen]);

  const getMoves = async () => {
    setLoading(true);
    let items = [];
    try {
      const res = await moveGetAll('name', 'asc', searchedString, 'all', 1, 1000);
      if (res.items) {
        items = res.items;
        setMoves(res.items);
        setTotal(res.total);
      } else {
        setMoves([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching moves:', error);
      setMoves([]);
      setTotal(0);
    }
    setLoading(false);
    return items;
  };

  const handleMoveToggle = (move: any) => {
    const isSelected = selectedMoves.some(selectedMove => selectedMove.id === move.id);
    if (isSelected) {
      dispatch(removeSelectedMove(move.id));
    } else {
      dispatch(addSelectedMove(move));
    }
  };

  const clearSearch = () => {
    setSearchedString('');
  };

  // Remove client-side filtering since backend now handles multi-field search
  const displayMoves = moves;

  return (
    <Grid item xs={4} sx={{
      backgroundColor: "rgba(39, 171, 109, 0.25)",
      overflowY: "scroll"
    }} padding={0} spacing={2} height={height}>
      <Grid container spacing={2} padding={4} sx={{ paddingLeft: 2 }}>
        <Grid item xs={12} sx={{ paddingLeft: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" style={{ color: "rgb(39, 171, 109)" }}>
              {t('Select Actions')}
              <Chip
                color='default'
                label={`${selectedMoves.length}/${total}`}
                size='small'
                variant='filled'
                sx={{ ml: 1 }}
              />
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => onHide?.()}
              sx={{ minWidth: 'auto', px: 2, py: 0.5, fontSize: '0.75rem' }}
            >
              {t('Hide')}
            </Button>
          </Box>
        </Grid>

        {selectedMoves.length > 0 && (
          <Grid item xs={12} sx={{ paddingLeft: 0 }}>
            <Box sx={{
              backgroundColor: 'rgba(39, 171, 109, 0.1)',
              borderRadius: 1,
              p: 1,
              mb: 1
            }}>
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('Selected Actions')} ({selectedMoves.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selectedMoves.map((move) => (
                  <Chip
                    key={move.id}
                    label={move.name}
                    size="small"
                    onDelete={() => dispatch(removeSelectedMove(move.id))}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
              {selectedMoves.length > 0 && (
                <Button
                  size="small"
                  color="error"
                  onClick={() => dispatch(clearSelectedMoves())}
                  sx={{ mt: 1 }}
                >
                  {t('Clear All')}
                </Button>
              )}
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sx={{ paddingLeft: 0 }}>
          <Button
            variant='contained'
            size='small'
            fullWidth
            onClick={() => {
              createInitiatedRef.current = true;
              existingMoveIdsRef.current = new Set(moves.map((move) => move.id));
              dispatch(openMoveDialog({ element: { id: null }, isEditabled: true, isDuplicated: false }));
            }}
            style={{ backgroundColor: 'rgb(39, 171, 109)' }}
          >
            {t('New action')}
          </Button>
        </Grid>

        <Grid item xs={12} sx={{ paddingLeft: 0 }}>
          <TextField
            fullWidth
            placeholder={t('Search actions by name, comment, or description...')}
            name="search"
            onChange={(e) => setSearchedString(e.target.value)}
            type="text"
            value={searchedString}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchedString && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box sx={{
            maxHeight: height - 200,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: '3px',
              '&:hover': {
                background: '#a8a8a8',
              },
            },
          }}>
            {loading ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
                color: 'text.secondary'
              }}>
                <Typography variant="body2">
                  {t('Loading actions...')}
                </Typography>
              </Box>
            ) : displayMoves.length === 0 ? (
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                py: 4,
                color: 'text.secondary'
              }}>
                <Typography variant="body2">
                  {searchedString ? t('No actions found matching your search') : t('No actions available')}
                </Typography>
              </Box>
            ) : (
              displayMoves.map((move) => {
                const isSelected = selectedMoves.some(selectedMove => selectedMove.id === move.id);
                return (
                  <Box
                    key={move.id}
                    sx={{
                      backgroundColor: isSelected
                        ? 'rgba(39, 171, 109, 0.08)'
                        : 'background.paper',
                      border: isSelected
                        ? '2px solid rgb(39, 171, 109)'
                        : '1px solid #e0e0e0',
                      borderRadius: 2,
                      marginBottom: 1.5,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor: isSelected
                          ? 'rgba(39, 171, 109, 0.12)'
                          : 'rgba(39, 171, 109, 0.04)',
                        borderColor: isSelected
                          ? 'rgb(39, 171, 109)'
                          : 'rgba(39, 171, 109, 0.3)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
                      cursor: 'pointer',
                    }}
                    onClick={() => handleMoveToggle(move)}
                  >
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      p: 2,
                      position: 'relative'
                    }}>
                      {/* Selection Checkbox */}
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mr: 1.5,
                        mt: 0.5
                      }}>
                        <Checkbox
                          checked={isSelected}
                          size="small"
                          sx={{
                            p: 0,
                            color: isSelected ? 'rgb(39, 171, 109)' : 'text.secondary',
                            '&.Mui-checked': {
                              color: 'rgb(39, 171, 109)',
                            },
                          }}
                          color="primary"
                        />
                      </Box>

                      {/* Move Content */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: isSelected ? 600 : 500,
                            color: isSelected ? 'rgb(39, 171, 109)' : 'text.primary',
                            mb: 0.5,
                            lineHeight: 1.3,
                          }}
                        >
                          {move.name}
                        </Typography>

                        {move.comment && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                              fontSize: '0.75rem',
                            }}
                          >
                            {move.comment}
                          </Typography>
                        )}

                        {move.descriptionOfTheMove && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.4,
                              fontSize: '0.7rem',
                              mt: 0.5,
                              fontStyle: 'italic',
                            }}
                          >
                            {move.descriptionOfTheMove}
                          </Typography>
                        )}
                      </Box>

                      {/* Actions Menu */}
                      <Box sx={{
                        ml: 1,
                        opacity: 0.7,
                        '&:hover': {
                          opacity: 1,
                        },
                      }}>
                        <TableItemMenu
                          onView={() => dispatch(openMoveDialog({ element: move, isEditabled: false, isDuplicated: false }))}
                          onEdit={() => dispatch(openMoveDialog({ element: move, isEditabled: true, isDuplicated: false }))}
                          onDuplicate={() => dispatch(openMoveDialog({ element: move, isEditabled: true, isDuplicated: true }))}
                          onDelete={() => {
                            // Handle delete functionality if needed
                            console.log('Delete move:', move.id);
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Selection Indicator */}
                    {isSelected && (
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        backgroundColor: 'rgb(39, 171, 109)',
                      }} />
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Grid>
      </Grid>
    </Grid>
  );
};